import dayjs from 'dayjs';

import { ControlDataGerlComponent } from '#components/control-data/control-data-gerl/control-data-gerl.component';
import { ESGGerlEscalationModalComponent } from '#modals/esg-gerl-escalation-modal';
import {
  UltimateParentSearchModalComponent,
  UltimateParentSearchModalData,
} from '#modals/ultimate-parent-search-modal/ultimate-parent-search-modal.component';
import { EsgGerlUpdateModalComponent } from '#modals/update-modal/esg-gerl-update-modal/esg-gerl-update-modal.component';
import { ReputationalRiskUpdateModalComponent } from '#modals/update-modal/reputational-risk-update-modal/reputational-risk-update-modal.component';
import { SensitiveBusinessUpdateModalComponent } from '#modals/update-modal/sensitive-business-update-modal/sensitive-business-update-modal.component';
import { ControllerStatus } from '#models/ControllerStatus.enum';
import { GERLCompany, GerlRejectCode } from '#models/Gerl.model';
import {
  ControllerCode,
  FormattedPublicData,
  getMicroControllerData,
  getMicroControllerDataFromDetails,
  MicroController,
  MicroControllerConfig,
  Notification,
  NotificationContext,
  TradeSector,
} from '#models/MicroController.model';
import { ControllerDetails, hasCheckData } from '#services/cve/ControllerDetails';
import { IterationStatusCode, VettingIteration } from '#services/cve/VettingIteration';
import { formatDate } from '#utils/dateUtils';

import { CompanyGradeExpirationCode } from './CompanyGradeExpirationCode.enum';
import {
  CompanyGradeExpirationNotificationComponent,
  CompanyGradeExpiredNotificationComponent,
  CompanyGradeInformationNotificationComponent,
  CompanyGradeMissingNotificationComponent,
  CompanyGradeUnderReviewNotificationComponent,
  EnergyPolicyNotificationComponent,
  GenericNeedRerunNotificationComponent,
  GenericPendingTooLongNotificationComponent,
  GerlAccountNotFoundNotificationComponent,
  GerlFileComponent,
  GerlRelatedListedNotificationComponent,
  GerlUltimateParentUnknownNotificationComponent,
  PEPNotificationComponent,
  ReputationalRiskNotificationComponent,
  SensitiveCountryNotificationComponent,
} from './microControllersNotifications.data';
import { ServicesInjector } from './ServicesInjector';

export function isGerlAdditionalCheckAllowed(gerlDetails: ControllerDetails<'Gerl'>) {
  return (
    gerlDetails.statusCode !== ControllerStatus.ESCALATING &&
    !(hasCheckData(gerlDetails) && gerlDetails.check.controlData.rejectCode === GerlRejectCode.ACCOUNT_NOT_FOUND)
  );
}

export const microControllers: {
  [key in ControllerCode]: MicroControllerConfig<key>;
} = {
  EconomicSanctions: {
    code: 'EconomicSanctions',
    i18n: 'control.data.economicSanctions.name',
    isRerunCheckDisplayed: () => true,
  },
  PoliticalExposedPerson: {
    code: 'PoliticalExposedPerson',
    i18n: 'control.data.politicalExposedPerson.name',
    manual: true,
    isExcludedForBuCodes: ['HERM', 'HSWI', 'SIAC', 'EHZA', 'CSEC', 'EHTR', 'EHEA'],
    specificNotificationsDisplay: (data) => {
      const detailsNotifications: Notification[] = [];
      if (data.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED) {
        detailsNotifications.push({
          message: PEPNotificationComponent,
          context: NotificationContext.INFO,
        });
      }
      return {
        details: detailsNotifications,
        modal: [],
      };
    },
  },
  CompanyGrade: {
    code: 'CompanyGrade',
    i18n: 'control.data.companyGrade.name',
    isRerunCheckDisplayed: () => true,
    hideIsExpiredWarning: true,
    publicDataFormatter: async (data) => {
      const dateText = data.grade ? formatDate(data.grade.lastRefreshDate) : '-';
      const gradeText = data.grade ? data.grade.gradeCode : '-';
      return [
        {
          i18n: 'control.data.companyGrade.grade-date',
          testId: 'grade-date',
          data: [{ text: dateText }],
        },
        {
          i18n: 'control.data.companyGrade.irp-grade',
          testId: 'irp-grade',
          data: [{ text: gradeText }],
        },
      ];
    },
    specificNotificationsDisplay: (controller, services, iterationData) => {
      const data = getMicroControllerData(controller);
      const controllerDetails = controller.controllerDetails;

      if (!data || !hasCheckData(controllerDetails)) {
        return {
          details: [],
          modal: [],
        };
      }
      const expirationDateTime = controllerDetails.check.expirationDateTime;
      const detailsNotifications: Notification[] = [];

      const gradeAutoRefreshEnabled = services.featureToggleService.getFeatureToggleSignal('gradeAutoRefresh')();

      if (data.expirationCode === CompanyGradeExpirationCode.LAST_REFRESH_UP_TO_DATE) {
        const daysBeforeExpiration = dayjs(expirationDateTime).diff(dayjs(), 'days');
        if (daysBeforeExpiration < 31) {
          detailsNotifications.push({
            message: CompanyGradeExpirationNotificationComponent,
            messageInputs: { daysBeforeExpiration: daysBeforeExpiration.toString() },
            context: NotificationContext.WARNING,
          });
        }
      } else if (data.expirationCode && iterationData?.statusCode === IterationStatusCode.CREATED) {
        if (gradeAutoRefreshEnabled) {
          detailsNotifications.push({
            message: CompanyGradeUnderReviewNotificationComponent,
            context: NotificationContext.INFO,
          });
        } else if (
          data.expirationCode === CompanyGradeExpirationCode.LAST_REFRESH_TOO_OLD &&
          iterationData?.statusCode === IterationStatusCode.CREATED
        ) {
          detailsNotifications.push({
            message: CompanyGradeExpiredNotificationComponent,
            context: NotificationContext.WARNING,
          });
          detailsNotifications.push({
            message: CompanyGradeInformationNotificationComponent,
            context: NotificationContext.INFO,
          });
        } else if (
          data.expirationCode === CompanyGradeExpirationCode.NO_REFRESH_DATE &&
          iterationData?.statusCode === IterationStatusCode.CREATED
        ) {
          detailsNotifications.push({
            message: CompanyGradeMissingNotificationComponent,
            context: NotificationContext.WARNING,
          });
          detailsNotifications.push({
            message: CompanyGradeInformationNotificationComponent,
            context: NotificationContext.INFO,
          });
        }
      }
      return {
        details: detailsNotifications,
        modal: [],
      };
    },
  },
  Gerl: {
    code: 'Gerl',
    i18n: 'control.data.gerl.name',
    manual: true,
    additionalCheck: {
      modalComponent: UltimateParentSearchModalComponent,
      modalInputData: {
        resultButtonI18n: 'additional-check.gerl.check-ultimate-parent',
        titleI18n: 'additional-check.gerl.title',
      } satisfies UltimateParentSearchModalData,
      isAllowed: (controller) => isGerlAdditionalCheckAllowed(controller.controllerDetails),
      isRequiredForUpdate: (controller) => {
        if (
          !hasCheckData(controller.controllerDetails) ||
          controller.controllerDetails.check.controlData.requestData?.['ultimateParentCompanyId']
        ) {
          return false;
        }
        const gerlCompanies = getMicroControllerDataFromDetails(controller.controllerDetails).gerl?.companies;
        return !gerlEhidsFormatter(gerlCompanies).at(1);
      },
    },
    customUpdate: {
      modalComponent: EsgGerlUpdateModalComponent,
    },
    customEscalation: {
      modalComponent: ESGGerlEscalationModalComponent,
    },
    isRerunCheckDisplayed: (controller) =>
      controller.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED &&
      controller.controllerDetails.check.controlData.rejectCode === GerlRejectCode.ACCOUNT_NOT_FOUND,
    specificNotificationsDisplay: (controller) => {
      const data = getMicroControllerData(controller);
      const isManual = controller.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED;
      if (!data || !hasCheckData(controller.controllerDetails) || !isManual) {
        return {};
      }

      const detailsNotifications: Notification[] = [];
      const rejectCode = controller.controllerDetails.check.controlData.rejectCode;

      if (rejectCode === GerlRejectCode.ACCOUNT_NOT_FOUND) {
        detailsNotifications.push({
          message: GerlAccountNotFoundNotificationComponent,
          context: NotificationContext.WARNING,
        });
      } else {
        detailsNotifications.push({
          message: GerlFileComponent,
          context: NotificationContext.WARNING,
        });
        if (rejectCode === GerlRejectCode.RELATED_COMPANY_LISTED) {
          detailsNotifications.push({
            message: GerlRelatedListedNotificationComponent,
            context: NotificationContext.WARNING,
          });
        } else if (rejectCode === GerlRejectCode.ULTIMATE_PARENT_COMPANY_UNKNOWN) {
          detailsNotifications.push({
            message: GerlUltimateParentUnknownNotificationComponent,
            context: NotificationContext.WARNING,
          });
        }
      }
      return {
        details: detailsNotifications,
        modal: [],
      };
    },
    commentTemplate: (controller) => {
      if (
        hasCheckData(controller.controllerDetails) &&
        controller.controllerDetails.check.controlData.rejectCode === GerlRejectCode.ACCOUNT_NOT_FOUND
      ) {
        return `GERL last update date: -
Is the company listed in the GERL?: -
Is the company the Ultimate Parent?: -
Is the Ultimate Parent listed into the GERL?: -
Are there related entities listed into the GERL?: - `;
      }
      return undefined;
    },
    publicDataFormatter: async (data) =>
      [
        {
          i18n: 'control.data.gerl.last-update-date',
          data: [{ text: data.gerl?.lastUpdateDate === undefined ? '-' : formatDate(data.gerl.lastUpdateDate) }],
          testId: 'gerl-last-update-date',
        },
        {
          i18n: 'control.data.gerl.is-listed',
          data: gerlBooleanFormatter(
            data.gerl?.isGerlCompliant === undefined ? undefined : !data.gerl?.isGerlCompliant,
          ),
          testId: 'gerl-is-company-listed',
        },
        {
          i18n: 'control.data.gerl.is-ultimate-parent',
          data: gerlBooleanFormatter(data.gerl?.isUltimateParent),
          testId: 'gerl-is-ultimate-parent',
        },
        {
          i18n: 'control.data.gerl.is-up-listed',
          data: gerlBooleanFormatter(
            data.gerl?.isUpGerlCompliant === undefined ? undefined : !data.gerl?.isUpGerlCompliant,
          ),
          testId: 'gerl-is-up-listed',
        },
        {
          i18n: 'control.data.gerl.are-related-listed',
          data: gerlBooleanFormatter(data.gerl?.isRelatedCompaniesListed),
          testId: 'gerl-are-related-listed',
        },
        {
          i18n: 'control.data.gerl.ehid',
          data: gerlEhidsFormatter(data.gerl?.companies),
          testId: 'gerl-ehid',
        },
      ] satisfies FormattedPublicData[],
    publicDataComponent: ControlDataGerlComponent,
  },
  TradeSector: {
    code: 'TradeSector',
    i18n: 'control.data.trade-sector.name',
    publicDataFormatter: tradeSectorsFormatter,
  },
  EnergyPolicy: {
    code: 'EnergyPolicy',
    i18n: 'control.data.energyPolicy.name',
    publicDataFormatter: tradeSectorsFormatter,
    hasQuiz: true,
    customEscalation: {
      modalComponent: ESGGerlEscalationModalComponent,
    },
    specificNotificationsDisplay(controller) {
      const notificationsDetails: Notification[] = [];
      if (controller.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED) {
        notificationsDetails.push({
          message: EnergyPolicyNotificationComponent,
          context: NotificationContext.INFO,
        });
      }
      return { details: notificationsDetails, modal: [] };
    },
  },
  SensitiveBusiness: {
    code: 'SensitiveBusiness',
    i18n: 'control.data.sensitiveBusiness.name',
    customUpdate: {
      modalComponent: SensitiveBusinessUpdateModalComponent,
    },
    customEscalation: {
      modalComponent: ESGGerlEscalationModalComponent,
    },
    publicDataFormatter: tradeSectorsFormatter,
  },
  SensitiveCountry: {
    code: 'SensitiveCountry',
    i18n: 'control.data.sensitiveCountry.name',
    hasQuiz: true,
    customEscalation: {
      modalComponent: ESGGerlEscalationModalComponent,
    },
    publicDataFormatter: async (data, injector) => {
      const { countryCode } = data;
      const countries = await injector.dataRefService.getCountriesWithCache();
      return [
        {
          i18n: 'control.data.sensitiveCountry.country',
          data: [{ text: countries.find((c) => c.countryCode === countryCode)?.countryCommonLabel ?? countryCode }],
        },
      ];
    },
    specificNotificationsDisplay(controller) {
      const notificationsDetails: Notification[] = [];
      if (controller.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED) {
        notificationsDetails.push({
          message: SensitiveCountryNotificationComponent,
          context: NotificationContext.INFO,
        });
      }
      return { details: notificationsDetails, modal: [] };
    },
  },
  ReputationalRisk: {
    code: 'ReputationalRisk',
    i18n: 'control.data.reputationalRisk.name',
    manual: true,
    customUpdate: {
      modalComponent: ReputationalRiskUpdateModalComponent,
    },
    escalationResponseDuration: [1, 'year'],
    specificNotificationsDisplay: (controller) => {
      const detailsNotifications: Notification[] = [];
      const modalNotifications: Notification[] = [];
      if (controller.controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED) {
        detailsNotifications.push({
          message: ReputationalRiskNotificationComponent,
          context: NotificationContext.INFO,
        });
      }
      return {
        details: detailsNotifications,
        modal: modalNotifications,
      };
    },
  },
  CompanyConsortOrCoop: {
    code: 'CompanyConsortOrCoop',
    i18n: 'control.data.companyConsortOrCoop.name',
    hasEscalationEmailInput: true,
  },
  AccountDetails: {
    code: 'AccountDetails',
    i18n: 'control.data.accountDetails.name',
    hasEscalationEmailInput: true,
  },
  AccountOwner: {
    code: 'AccountOwner',
    i18n: 'control.data.accountOwner.name',
    hasEscalationEmailInput: true,
  },
  PreviousNegociation: {
    code: 'PreviousNegociation',
    i18n: 'control.data.previousNegociation.name',
    hasEscalationEmailInput: true,
  },
  PreviousContract: {
    code: 'PreviousContract',
    i18n: 'control.data.previousContract.name',
    hasEscalationEmailInput: true,
  },
  ProspectLocation: {
    code: 'ProspectLocation',
    i18n: 'control.data.prospectLocation.name',
    hasEscalationEmailInput: true,
  },
  BuExchangeGroup: {
    code: 'BuExchangeGroup',
    i18n: 'control.data.buExchangeGroup.name',
    hasEscalationEmailInput: true,
  },
  MncList: {
    code: 'MncList',
    i18n: 'control.data.mncList.name',
    hasEscalationEmailInput: true,
  },
  UnpaidPremiumAndFees: {
    code: 'UnpaidPremiumAndFees',
    i18n: 'control.data.unpaidPremiumAndFees.name',
    hasEscalationEmailInput: true,
  },
};

export function genericNotificationsDisplay<T extends ControllerCode>(
  controller: MicroController<T>,
  iterationData: VettingIteration | undefined,
) {
  const notifications: Notification[] = [];
  if (
    controller.isExpired &&
    !controller.hideIsExpiredWarning &&
    iterationData?.statusCode === IterationStatusCode.CREATED
  ) {
    notifications.push({
      message: GenericNeedRerunNotificationComponent,
      context: NotificationContext.WARNING,
    });
  }
  if (isPendingForTooLong(controller)) {
    notifications.push({ context: NotificationContext.ERROR, message: GenericPendingTooLongNotificationComponent });
  }
  return notifications;
}

export function isPendingForTooLong(controller: MicroController) {
  if (controller.controllerDetails.statusCode !== ControllerStatus.CHECKING) {
    return false;
  }
  return dayjs().diff(dayjs(controller.controllerDetails.check.requestDateTime), 'minutes') >= 5;
}

async function tradeSectorsFormatter(data: { tradeSectors: TradeSector[] }, services: ServicesInjector) {
  const { dataRefService } = services;
  const { tradeSectors } = data;
  const labeledTradeSectors = await Promise.all(
    tradeSectors.map(async (tradeSector) => {
      let label;
      try {
        label = (await dataRefService.getTradeSector(tradeSector.code)).tradeSectorLabel;
      } catch (error) {
        console.warn(`Failed to get trade sector label for ${tradeSector.code}`, error);
      }
      return {
        ...tradeSector,
        label,
      };
    }),
  );
  const mainTradeSectorIndex = labeledTradeSectors.findIndex((tradeSector) => tradeSector.isMain);

  const formattedData: FormattedPublicData[] = [];
  if (mainTradeSectorIndex !== -1) {
    const mainTradeSector = labeledTradeSectors.splice(mainTradeSectorIndex, 1)[0];
    formattedData.push({
      i18n: 'control.data.trade-sector.main-trade-sector',
      data: [
        { text: mainTradeSector.label ?? '-', style: mainTradeSector.isRestricted ? 'bold' : undefined },
        {
          text: `(${mainTradeSector.code})`,
          style: mainTradeSector.isRestricted ? 'bold' : 'light',
          iconName: mainTradeSector.isRestricted ? 'exclamation-circle-warning' : undefined,
        },
      ],
    });
  }
  formattedData.push({
    i18n: 'control.data.trade-sector.trade-sectors',
    testId: 'trade-sectors',
    data: labeledTradeSectors
      .map(
        (tradeSector) =>
          [
            { text: tradeSector.label ?? '-', style: tradeSector.isRestricted ? 'bold' : undefined },
            {
              text: `(${tradeSector.code})`,
              style: tradeSector.isRestricted ? 'bold' : 'light',
              iconName: tradeSector.isRestricted ? 'exclamation-circle-warning' : undefined,
            },
          ] as const,
      )
      .flat(),
  });
  return formattedData;
}

function gerlBooleanFormatter(value: boolean | undefined): FormattedPublicData['data'] {
  if (value === undefined) {
    return [{ text: '-' }];
  }
  return [
    value
      ? { text: 'control.data.gerl.yes', mustTranslate: true }
      : { text: 'control.data.gerl.no', mustTranslate: true },
  ];
}

function gerlEhidsFormatter(companies?: GERLCompany[]): FormattedPublicData['data'] {
  if (!companies) {
    return [{ text: '-' }];
  }
  return companies.toSorted((company) => (company.type === 'MAIN' ? -1 : 0)).map((company) => ({ text: company.id }));
}
